import { Injectable } from '@angular/core';
import * as CryptoJS from 'crypto-js';
import Dexie, { liveQuery } from 'dexie';
import 'dexie-observable';
import * as moment from 'moment';
import { BehaviorSubject, from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { EnvService } from './env.service';

class ContentDB extends Dexie {
  public contents: Dexie.Table<any, string>;
  public chunks: Dexie.Table<any, string>;
  public courses: Dexie.Table<any, string>;
  public courseList: Dexie.Table<any, string>;
  public blogs: Dexie.Table<any, string>;
  public blogList: Dexie.Table<any, string>;
  public notes: Dexie.Table<any, string>;
  public notesList: Dexie.Table<any, string>;
  public images: Dexie.Table<any, string>;
  public audios: Dexie.Table<any, string>;
  public chunkCreator: Dexie.Table<any, string>;
  public contentsCurrentAudio: Dexie.Table<any, string>;
  public contentsCurrentAudioDuration: Dexie.Table<any, string>;
  public conversations: Dexie.Table<any, string>;

  public constructor() {
    super('ContentDB');
    this.version(27).stores({
      chunks: '++_id,_ts',
      contents: '++_id,_ts',
      courses: '++_id,_ts',
      courseList: '++_id,_ts',
      notes: '++_id,_ts',
      notesList: '++_id,_ts',
      blogList: '++_id,_ts',
      images: '++_id,_ts',
      audios: '++_id,_ts',
      chunkCreator: '++_id,_ts',
      blogs: '++_id,_ts',
      contentsCurrentAudio: '++_id,_ts',
      contentsCurrentAudioDuration: '++_id,_ts',
      conversations: '++_id,_ts',
    });
    this.chunks = this.table('notes');
    this.chunks = this.table('chunks');
    this.contents = this.table('contents');
    this.courses = this.table('courses');
    this.courseList = this.table('courseList');
    this.blogs = this.table('blogs');
    this.blogList = this.table('blogList');
    this.images = this.table('images');
    this.audios = this.table('audios');
    this.notes = this.table('notes');
    this.notesList = this.table('notesList');
    this.chunkCreator = this.table('chunkCreator');
    this.contentsCurrentAudio = this.table('contentsCurrentAudio');
    this.contentsCurrentAudioDuration = this.table('contentsCurrentAudioDuration');
    this.conversations = this.table('conversations');
    let _ts = moment().unix();
    let tsLimit = moment().subtract(1, 'w').unix();
    const limit = 5000;
    const imgLimit = 500;
    const audioLimit = 1000;
    let tables = ['chunks', 'contents', 'courseList', 'blogs', 'blogList', 'images', 'audios', 'notes', 'notesList'];

    this.chunkCreator.filter((chunk) => chunk._ts < tsLimit).delete();

    this.chunks.count().then((count) => {
      if (count > limit) {
        this.chunks
          .where('_ts')
          .below(_ts)
          .limit(count - limit)
          .delete();
      }
      this.chunks.filter((chunk) => chunk._ts < tsLimit).delete();
    });

    this.images.count().then((count) => {
      if (count > imgLimit) {
        this.images
          .where('_ts')
          .below(_ts)
          .limit(count - imgLimit)
          .delete();
      }
      this.images.filter((image) => image._ts < tsLimit).delete();
    });

    this.audios.count().then((count) => {
      if (count > audioLimit) {
        this.audios
          .where('_ts')
          .below(_ts)
          .limit(count - audioLimit)
          .delete();
      }
      this.audios.filter((audio) => audio._ts < tsLimit).delete();
    });

    this.contentsCurrentAudio.count().then((count) => {
      if (count > audioLimit) {
        this.contentsCurrentAudio
          .where('_ts')
          .below(_ts)
          .limit(count - audioLimit)
          .delete();
      }
      this.contentsCurrentAudio.filter((audio) => audio._ts < tsLimit).delete();
    });

    this.contents.filter((content) => content._ts < tsLimit).delete();
    for (let table of tables) {
      this.table(table)
        .filter((content) => !content._ts)
        .delete();
    }
  }
}

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  db = new ContentDB();

  private updatedCacheDataSource = new BehaviorSubject({
    table: null,
    id: null,
  });
  updatedCacheData$ = this.updatedCacheDataSource.asObservable();

  constructor(private env: EnvService) {
    this.db.on('changes', (changes) => {
      changes.forEach((change) => {
        this.sendDexieTableChange(change.table, change.key);
      });
    });
  }

  sendDexieTableChange(table, id) {
    this.updatedCacheDataSource.next({ table, id });
  }

  async clear(collection) {
    return new Promise((resolve) => {
      this.db
        .transaction('rw', this.db[collection], async () => {
          let data = await this.db[collection].clear();
          resolve(data);
        })
        .catch((e) => {
          console.error(e.stack || e);
        });
    });
  }

  async remove(collection, IDs) {
    return new Promise((resolve) => {
      this.db
        .transaction('rw', this.db[collection], async () => {
          let data = await this.db[collection].where('_id').anyOfIgnoreCase(IDs).delete();
          resolve(data);
        })
        .catch((e) => {
          console.error(e.stack || e);
        });
    });
  }

  async getAll(collection) {
    return new Promise((resolve) => {
      this.db
        .transaction('rw', this.db[collection], async () => {
          let data = await this.db[collection].toArray();
          data = data.map((element) => {
            if (element.__data) {
              element = JSON.parse(CryptoJS.AES.decrypt(element.__data, this.env.appName).toString(CryptoJS.enc.Utf8));
              element.decrypted = true;
              return element;
            } else {
              return element;
            }
          });
          resolve(data);
        })
        .catch((e) => {
          resolve([]);
          console.error(e.stack || e);
        });
    });
  }

  async find(collection: string, IDs: Array<string>): Promise<any[]> {
    IDs = IDs.filter((id) => id);
    return new Promise((resolve) => {
      this.db
        .transaction('rw', this.db[collection], async () => {
          let data = await this.db[collection].where('_id').anyOfIgnoreCase(IDs).toArray();
          data = data.map((element) => {
            if (element.__data) {
              element = JSON.parse(CryptoJS.AES.decrypt(element.__data, this.env.appName).toString(CryptoJS.enc.Utf8));
              element.decrypted = true;
              return element;
            } else {
              return element;
            }
          });
          resolve(data);
        })
        .catch((e) => {
          resolve([]);
          console.error(collection, IDs, e);
        });
    });
  }

  find$(collection: string, IDs: Array<string>): Observable<any[]> {
    const filteredIDs = IDs.filter((id) => id);

    return from(liveQuery(() => this.db[collection].where('_id').anyOfIgnoreCase(filteredIDs).toArray())).pipe(
      map((data) =>
        data.map((element) => {
          if (element.__data) {
            element = JSON.parse(CryptoJS.AES.decrypt(element.__data, this.env.appName).toString(CryptoJS.enc.Utf8));
            element.decrypted = true;
            return element;
          } else {
            return element;
          }
        })
      )
    );
  }

  update(collection, id, update) {
    return new Promise(async (resolve, reject) => {
      let documents = await this.find(collection, [id]);
      let documentToUpdate = documents[0];
      if (documentToUpdate?.decrypted) {
        for (let updateElement in update) {
          documentToUpdate[updateElement] = update[updateElement];
        }
        let newEncryptedData = CryptoJS.AES.encrypt(JSON.stringify(documentToUpdate), this.env.appName).toString();
        let doc = await this.db[collection].where('_id').equals(id).modify({ __data: newEncryptedData });
        resolve(doc);
      } else {
        this.db[collection]
          .where('_id')
          .equals(id)
          .modify(update)
          .then((doc) => {
            resolve(doc);
          })
          .catch((err) => {
            reject(err);
          });
      }
    });
  }

  store(collection, data) {
    var encrypted = CryptoJS.AES.encrypt(JSON.stringify(data), this.env.appName).toString();
    return this.db[collection].put({ _id: data._id, __data: encrypted, _ts: moment().unix() }, [data._id]);
  }

  bulkStore(collection: string, data: any[]): Promise<any> {
    const itemsToPut = data.map((item) => {
      const encrypted = CryptoJS.AES.encrypt(JSON.stringify(item), this.env.appName).toString();
      return { _id: item._id, __data: encrypted, _ts: moment().unix() };
    });
    return this.db[collection].bulkPut(itemsToPut);
  }

  bulkUpdate(collection: string, data: any[]): Promise<any> {
    const updates = data.map((item) => {
      const encrypted = CryptoJS.AES.encrypt(JSON.stringify(item), this.env.appName).toString();
      return { _id: item._id, __data: encrypted, _ts: moment().unix() };
    });
    return this.db[collection].bulkPut(updates);
  }

  bulkRemove(collection: string, ids: string[]): Promise<any> {
    return this.db[collection].bulkDelete(ids);
  }

  async updateChunkAndContent(chunk: any, content: any): Promise<void> {
    const _ts = moment().unix();
    if (chunk) {
      chunk._ts = _ts;
      await this.db.chunks.where('_id').equals(chunk._id).modify(chunk);
    }
    if (content) {
      content._ts = _ts;
      content.contents = content.contents.map((e) => ({ _id: e.chunk, __v: e.__v }));
      await this.db.contents.where('_id').equals(content._id).modify(content);
    }
  }
}
